import { Button, Flex, Text } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import { ModalWarningContent } from '@shared/ui/modal';
import { useDeleteUsers } from '@entities/user';
import { useNavigate } from 'react-router';
import { showNotification } from '@shared/utils';
import { QueryKey } from '@shared/config';
import { useQueryClient } from '@tanstack/react-query';

const MODAL_ID = 'confirm-delete-user';

type ConfirmDeleteModalComponentProps = {
  id: string;
  firstName: string;
  lastName: string;
};

const ConfirmDeleteModalComponent = ({
  id,
  firstName,
  lastName,
}: ConfirmDeleteModalComponentProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: deleteUser, isPending } = useDeleteUsers();

  const handleClose = () => {
    closeModal(MODAL_ID);
  };

  const handleDelete = () => {
    deleteUser(
      { ids: [Number(id)] },
      {
        onSuccess: () => {
          handleClose();

          queryClient.invalidateQueries({
            queryKey: [QueryKey.GET_USERS_WITH_PAGINATION],
          });

          showNotification({
            title: 'Сотрудник удален',
            message: (
              <Flex>
                <Text>Сотрудник&nbsp;</Text>
                <Text fw={600}>{`${firstName}, ${lastName}`}</Text>
                <Text>&nbsp;успешно удален</Text>
              </Flex>
            ),
            variant: 'info',
          });

          navigate('/employee-list');
        },
        onError: error => {
          showNotification({
            error,
            message:
              error.response?.data.errors.message ||
              'Не удалось удалить сотрудника',
            variant: 'error',
          });
        },
      }
    );
  };

  return (
    <Flex>
      <Flex direction='column'>
        <ModalWarningContent message='Подтвердите удаление пользователя, при этом все вакансии данного пользователя будут переданы менеджеру компании.' />

        <Flex pt={12} gap={8}>
          <Button variant='outline' onClick={handleClose}>
            Отмена
          </Button>
          <Button variant='primary' onClick={handleDelete} loading={isPending}>
            Удалить
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const useConfirmDeleteModal = () => {
  const openConfirmDeleteModal = (
    id: string,
    firstName: string,
    lastName: string
  ) =>
    openModal({
      modalId: MODAL_ID,
      title: 'Удалить сотрудника',
      children: (
        <ConfirmDeleteModalComponent
          id={id}
          firstName={firstName}
          lastName={lastName}
        />
      ),
    });

  return { openConfirmDeleteModal };
};
